import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { es } from "date-fns/locale";
import {
  Search,
  Filter,
  CalendarIcon,
  MessageSquare,
  Clock,
  User,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  TrendingUp,
  Brain,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const ChatsPage = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    agent: "",
    campaign: "",
    is_analyzed: "",
  });

  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });

  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [analyzing, setAnalyzing] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchChats = async (overrideSearch = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "25",
        date_from: format(dateRange.from, "yyyy-MM-dd HH:mm:ss"),
        date_to: format(dateRange.to, "yyyy-MM-dd HH:mm:ss"),
      });

      const currentSearch = overrideSearch !== null ? overrideSearch : search;
      if (currentSearch) params.append("search", currentSearch);
      if (filters.agent) params.append("agent", filters.agent);
      if (filters.campaign) params.append("campaign", filters.campaign);
      if (filters.is_analyzed !== "") params.append("is_analyzed", filters.is_analyzed);

      const response = await authAxios.get(`/chats?${params}`);
      setChats(response.data.chats || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching chats:", error);
      toast.error("Error al cargar chats de WhatsApp/SMS");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [agentsRes, campaignsRes] = await Promise.all([
        authAxios.get("/agents"),
        authAxios.get("/campaigns"),
      ]);
      setAgents(agentsRes.data || []);
      setCampaigns(campaignsRes.data || []);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    const searchVal = searchParams.get("search") || "";
    setSearch(searchVal);
    fetchChats(searchVal);
  }, [page, dateRange, filters, searchParams]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setPage(1);
    if (search.trim()) {
      navigate(`/chats?search=${encodeURIComponent(search.trim())}`);
    } else {
      navigate(`/chats`);
    }
  };

  const handleAnalyze = async (chatId, e) => {
    if (e) e.stopPropagation();
    setAnalyzing(chatId);
    try {
      await authAxios.post(`/chats/${chatId}/analyze`);
      toast.success("Análisis cognitivo de chat completado");
      fetchChats();
    } catch (error) {
      console.error("Error analyzing chat:", error);
      toast.error("Error al analizar el chat");
    } finally {
      setAnalyzing(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const dateFrom = format(dateRange.from, "yyyy-MM-dd HH:mm:ss");
      const dateTo = format(dateRange.to, "yyyy-MM-dd HH:mm:ss");
      const response = await authAxios.post(`/chats/sync-records?date_from=${dateFrom}&date_to=${dateTo}`);
      toast.success(response.data.message || "Sincronización de chats finalizada");
      fetchChats();
    } catch (error) {
      console.error("Error syncing chats:", error);
      toast.error("Error al sincronizar chats");
    } finally {
      setSyncing(false);
    }
  };

  const setDatePreset = (preset) => {
    const today = new Date();
    if (preset === "today") {
      setDateRange({ from: startOfDay(today), to: endOfDay(today) });
    } else if (preset === "week") {
      setDateRange({ from: startOfWeek(today, { weekStartsOn: 1 }), to: endOfDay(today) });
    } else if (preset === "month") {
      setDateRange({ from: startOfMonth(today), to: endOfDay(today) });
    }
    setPage(1);
  };

  const getSentimentBadge = (sentiment) => {
    if (sentiment === undefined || sentiment === null) return <Badge variant="secondary">N/A</Badge>;
    const val = parseFloat(sentiment);
    if (val >= 7.5) return <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium shadow-sm">Excelente ({val})</Badge>;
    if (val >= 5.0) return <Badge className="bg-amber-500 hover:bg-amber-600 text-white font-medium shadow-sm">Neutral ({val})</Badge>;
    return <Badge className="bg-rose-500 hover:bg-rose-600 text-white font-medium shadow-sm">Negativo ({val})</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-emerald-600" />
            Chats y Mensajes
          </h1>
          <p className="text-slate-500 mt-1">
            Gestión y análisis cognitivo de conversaciones de WhatsApp y SMS.
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100/60 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-950">{total}</p>
              <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider">Conversaciones</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100/60 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-950">
                {chats.filter((c) => c.is_analyzed).length}
              </p>
              <p className="text-xs font-medium text-blue-700 uppercase tracking-wider">Analizadas con IA</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100/60 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-950">
                {chats.length > 0
                  ? (
                      chats
                        .filter((c) => c.is_analyzed && c.sentiment_score !== null)
                        .reduce((acc, curr) => acc + parseFloat(curr.sentiment_score), 0) /
                      (chats.filter((c) => c.is_analyzed && c.sentiment_score !== null).length || 1)
                    ).toFixed(1)
                  : "0.0"}
              </p>
              <p className="text-xs font-medium text-amber-700 uppercase tracking-wider">Sentiment Promedio</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-50 to-red-50 border-rose-100/60 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-rose-500 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-500/20">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rose-950">
                {chats.filter((c) => c.is_analyzed && parseFloat(c.sentiment_score) < 4.0).length}
              </p>
              <p className="text-xs font-medium text-rose-700 uppercase tracking-wider">Casos Críticos</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Date presets */}
      <Card className="border-slate-100 shadow-sm">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-500">Rango Rápido:</span>
            <Button variant="outline" size="sm" onClick={() => setDatePreset("today")}>Hoy</Button>
            <Button variant="outline" size="sm" onClick={() => setDatePreset("week")}>Esta Semana</Button>
            <Button variant="outline" size="sm" onClick={() => setDatePreset("month")}>Este Mes</Button>

            <div className="ml-auto flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-left font-normal flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-slate-500" />
                    {dateRange.from ? format(dateRange.from, "PPP", { locale: es }) : "Desde"} - {dateRange.to ? format(dateRange.to, "PPP", { locale: es }) : "Hasta"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <div className="p-3 border-b flex gap-2">
                    <Calendar
                      mode="range"
                      selected={{ from: dateRange.from, to: dateRange.to }}
                      onSelect={(range) => {
                        if (range?.from && range?.to) {
                          setDateRange({ from: range.from, to: range.to });
                          setPage(1);
                        }
                      }}
                      locale={es}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <form onSubmit={handleSearch} className="relative col-span-1 md:col-span-2">
              <Input
                placeholder="Buscar por GUID, Teléfono o Contenido..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            </form>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[180px] justify-between text-left font-normal" data-testid="chats-agent-filter">
                  <span className="truncate">
                    {filters.agent
                      ? `Agentes (${filters.agent.split(",").length})`
                      : "Todos los Agentes"}
                  </span>
                  <Filter className="w-4 h-4 ml-2 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-2" align="start">
                <div className="space-y-1 max-h-[220px] overflow-y-auto pr-1">
                  <div 
                    className="flex items-center space-x-2 p-1.5 hover:bg-slate-100 rounded cursor-pointer transition-colors"
                    onClick={() => {
                      setFilters({ ...filters, agent: "" });
                      setPage(1);
                    }}
                  >
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                      checked={!filters.agent}
                      onChange={() => {}}
                    />
                    <span className="text-sm text-slate-700 font-semibold select-none">Todos los Agentes</span>
                  </div>
                  {agents.map((agent) => {
                    const selectedList = filters.agent ? filters.agent.split(",") : [];
                    const isSelected = selectedList.includes(agent);
                    return (
                      <div
                        key={agent}
                        className="flex items-center space-x-2 p-1.5 hover:bg-slate-100 rounded cursor-pointer transition-colors"
                        onClick={() => {
                          let newList;
                          if (isSelected) {
                            newList = selectedList.filter((a) => a !== agent);
                          } else {
                            newList = [...selectedList, agent];
                          }
                          setFilters({ ...filters, agent: newList.join(",") });
                          setPage(1);
                        }}
                      >
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                          checked={isSelected}
                          onChange={() => {}}
                        />
                        <span className="text-sm text-slate-700 truncate select-none" title={agent}>{agent}</span>
                      </div>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>

            <Select
              value={filters.is_analyzed}
              onValueChange={(val) => {
                setFilters({ ...filters, is_analyzed: val === "ALL" ? "" : val });
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Estado de Análisis" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value="true">Analizados</SelectItem>
                <SelectItem value="false">Pendientes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Chats Table */}
      <Card className="border-slate-100 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50/75">
              <TableRow>
                <TableHead className="font-semibold text-slate-700">Canal / GUID</TableHead>
                <TableHead className="font-semibold text-slate-700">Cliente</TableHead>
                <TableHead className="font-semibold text-slate-700">Agente</TableHead>
                <TableHead className="font-semibold text-slate-700">Campaña</TableHead>
                <TableHead className="font-semibold text-slate-700">Fecha</TableHead>
                <TableHead className="font-semibold text-slate-700 text-center">Sentiment</TableHead>
                <TableHead className="font-semibold text-slate-700 text-center">Mensajes</TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10">
                    <Loader2 className="w-8 h-8 text-sky-600 animate-spin mx-auto mb-2" />
                    <span className="text-slate-500">Cargando conversaciones...</span>
                  </TableCell>
                </TableRow>
              ) : chats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    No se encontraron conversaciones para el período seleccionado.
                  </TableCell>
                </TableRow>
              ) : (
                chats.map((chat) => (
                  <TableRow
                    key={chat.id}
                    className="hover:bg-slate-50/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/chats/${chat.id}`)}
                  >
                    <TableCell className="font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800 border-none">WA</Badge>
                        <span className="truncate max-w-[120px] text-xs font-mono text-slate-500">{chat.guid}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-700">{chat.callerid || "Desconocido"}</TableCell>
                    <TableCell className="text-slate-700">{chat.agent_name || chat.agent || "N/A"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{chat.campaign || "N/A"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">
                      {chat.chat_date ? format(new Date(chat.chat_date), "dd/MM/yyyy HH:mm") : "N/A"}
                    </TableCell>
                    <TableCell className="text-center">{getSentimentBadge(chat.sentiment_score)}</TableCell>
                    <TableCell className="text-center font-semibold text-slate-700">{chat.total_messages || 0}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleAnalyze(chat.id)}
                          disabled={analyzing === chat.id}
                        >
                          {analyzing === chat.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Brain className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/chats/${chat.id}`)}
                        >
                          Detalle
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-100 bg-slate-50/50">
              <span className="text-sm text-slate-500">
                Página {page} de {totalPages} ({total} registros)
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatsPage;
